/**
 * Browser Web Push helpers for the installed PWA.
 *
 * Pure-ish wrappers around the Push API + service worker registration, kept
 * dependency-free so the subscription lifecycle can be driven from a single
 * hook (`useFeedWebPush`). Actual delivery is server-side (see the Rust
 * `feed_push` worker); these helpers only manage the browser subscription.
 */

/** True when the browser supports service-worker-based Web Push. */
export function isWebPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current Notification permission, or `"unsupported"` when unavailable. */
export function currentPushPermission(): NotificationPermission | "unsupported" {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

/**
 * Decode a URL-safe base64 VAPID key into the `Uint8Array` the Push API expects
 * for `applicationServerKey`.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Return the active push subscription for this browser, if any. */
export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isWebPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * Ensure a push subscription exists, creating one with the given VAPID key when
 * absent. Assumes notification permission has already been granted.
 */
export async function ensureSubscription(
  vapidPublicKey: string,
): Promise<PushSubscription> {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    // Cast to BufferSource: our Uint8Array is backed by a plain ArrayBuffer,
    // but the DOM lib's generic default widens to `ArrayBufferLike`.
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  });
}

/** Extract the standard subscribe payload (endpoint + keys) from a subscription. */
export function subscriptionToPayload(
  subscription: PushSubscription,
): { endpoint: string; keys: { p256dh: string; auth: string } } {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? "",
      auth: json.keys?.auth ?? "",
    },
  };
}
